export type FeedbackSourcePage = 'homepage' | 'user_feedback';
export type TrainingFeedbackSourceArea =
  | 'overview' | 'diagnostic' | 'study_plan' | 'practice'
  | 'practice_flashcards' | 'practice_free_response' | 'graded_test'
  | 'case_study' | 'leaderboard';
export type HomepageSourceArea = 'chat' | 'recent_workflows' | 'workflow_picker' | 'general';
export type UserFeedbackSourceArea =
  | 'queue' | 'details' | 'statistics_filters' | 'chat_feedback_records' | 'general';
export type FeedbackSourceConfidence = 'high' | 'medium' | 'fallback';

type FeedbackSourceEvidence = {
  sourceConfidence: FeedbackSourceConfidence;
};

export type FeedbackSourceTuple = FeedbackSourceEvidence & (
  | { sourcePage: 'homepage'; sourceArea: HomepageSourceArea }
  | { sourcePage: 'user_feedback'; sourceArea: UserFeedbackSourceArea }
  | { sourcePage: 'training'; sourceArea: TrainingFeedbackSourceArea }
);

const TRAINING_AREAS = new Set<TrainingFeedbackSourceArea>([
  'overview', 'diagnostic', 'study_plan', 'practice', 'practice_flashcards',
  'practice_free_response', 'graded_test', 'case_study', 'leaderboard',
]);

export function trainingFeedbackSource(sourceArea: TrainingFeedbackSourceArea): FeedbackSourceTuple {
  if (!TRAINING_AREAS.has(sourceArea)) throw new TypeError('Invalid Training feedback source area');
  return { sourcePage: 'training', sourceArea, sourceConfidence: 'high' };
}

const HOMEPAGE_AREAS = new Set<Exclude<HomepageSourceArea, 'general'>>([
  'chat', 'recent_workflows', 'workflow_picker',
]);
const USER_FEEDBACK_AREAS = new Set<Exclude<UserFeedbackSourceArea, 'general'>>([
  'queue', 'details', 'statistics_filters', 'chat_feedback_records',
]);

function isHomepageArea(value: string): value is Exclude<HomepageSourceArea, 'general'> {
  return HOMEPAGE_AREAS.has(value as Exclude<HomepageSourceArea, 'general'>);
}

function isUserFeedbackArea(value: string): value is Exclude<UserFeedbackSourceArea, 'general'> {
  return USER_FEEDBACK_AREAS.has(value as Exclude<UserFeedbackSourceArea, 'general'>);
}

export class FeedbackSourceTracker {
  private latest: FeedbackSourceTuple | null = null;

  record(
    sourcePage: 'homepage',
    sourceArea: Exclude<HomepageSourceArea, 'general'>,
    sourceConfidence: Exclude<FeedbackSourceConfidence, 'fallback'>,
  ): void;
  record(
    sourcePage: 'user_feedback',
    sourceArea: Exclude<UserFeedbackSourceArea, 'general'>,
    sourceConfidence: Exclude<FeedbackSourceConfidence, 'fallback'>,
  ): void;
  record(
    sourcePage: FeedbackSourcePage,
    sourceArea: Exclude<HomepageSourceArea | UserFeedbackSourceArea, 'general'>,
    sourceConfidence: Exclude<FeedbackSourceConfidence, 'fallback'>,
  ): void {
    if (sourcePage === 'homepage') {
      if (!isHomepageArea(sourceArea)) throw new TypeError('Invalid Homepage feedback source area');
      this.latest = { sourcePage, sourceArea, sourceConfidence };
      return;
    }
    if (!isUserFeedbackArea(sourceArea)) {
      throw new TypeError('Invalid User Feedback source area');
    }
    this.latest = { sourcePage, sourceArea, sourceConfidence };
  }

  snapshot(sourcePage: FeedbackSourcePage): FeedbackSourceTuple {
    const current = this.latest;
    if (current && current.sourcePage === sourcePage) return current;
    return sourcePage === 'homepage'
      ? { sourcePage, sourceArea: 'general', sourceConfidence: 'fallback' }
      : { sourcePage, sourceArea: 'general', sourceConfidence: 'fallback' };
  }

  reset(): void {
    this.latest = null;
  }
}
